import { RequestStatus } from '@core/api/state';
import { Property } from '@core/models/properties';
import { ExtendedCatalogCrop, FieldSeasonData } from '@core/models/seasons';
import { datadogLogs } from '@datadog/browser-logs';
import { Lens } from '@dhmk/zustand-lens';
import { loadAssetsAndImages } from '@redux/imageAssets/thunks';
import type { RootState } from '@redux/reducers';
import { store as reduxStore } from '@redux/store';
import type { AnyAction } from 'redux';
import { EntityState, createEntityAdapter } from 'redux-ngrx-entity';
import type { ThunkDispatch } from 'redux-thunk';
import type { BoundStore } from '../store';
import { fetchSeasons } from './season.actions';
import { SeasonMode } from './season.models';

type SeasonProperty = Pick<Property, 'id' | 'country_code' | 'regulatory_zone' | 'org_id'>;

interface SeasonState extends EntityState<FieldSeasonData> {
  status: RequestStatus;
  mode: SeasonMode;
  catalogCrops: ExtendedCatalogCrop[];
  displayWithoutActiveSeason: boolean;
  selectedSeasonId?: string;
}

interface SeasonActions {
  fetch: (id: SeasonProperty) => void;
  // Re-reads the property seasons in place: no 'loading' status, so RootLayout does not restart
  // the whole load, and the selection is kept while the season still exists. Resolves with the
  // new seasons, or undefined when it did not run.
  refresh: (property: SeasonProperty) => Promise<FieldSeasonData[] | undefined>;
  updateOptions: (
    options: Partial<Pick<SeasonState, 'displayWithoutActiveSeason' | 'mode' | 'selectedSeasonId'>>
  ) => void;
  updateSeasons: (uploadedFieldSeasons: Array<FieldSeasonData>) => void;
}

export type SeasonSlice = SeasonState & SeasonActions;

const seasonAdapter = createEntityAdapter<FieldSeasonData>();

const initialState: SeasonState = seasonAdapter.getInitialState({
  status: 'idle',
  mode: 'active_seasons',
  displayWithoutActiveSeason: true,
  catalogCrops: [],
  selectedSeasonId: undefined,
});

// Only the latest fetch/refresh may write to the store.
let latestRequestId = 0;

const dispatchLoadAssetsAndImages = (propertyId: string) => {
  void (reduxStore.dispatch as ThunkDispatch<RootState, undefined, AnyAction>)(
    loadAssetsAndImages(propertyId)
  );
};

export const createSeasonSlice: Lens<SeasonSlice, BoundStore> = (set, get, api) => ({
  ...initialState,
  fetch: (data) => {
    const requestId = ++latestRequestId;
    set((state) => ({
      ...seasonAdapter.removeAll(state),
      ...initialState,
      status: 'loading',
    }));
    fetchSeasons(data)
      .then(({ catalogCrops, filteredData }) => {
        if (requestId !== latestRequestId) return;
        set((state) => ({
          ...seasonAdapter.upsertMany(filteredData, state),
          catalogCrops,
          status: 'success',
        }));
      })
      .catch(() => {
        if (requestId !== latestRequestId) return;
        set((state) => ({ ...seasonAdapter.removeAll(state), status: 'error' }));
      });
  },
  refresh: async (data) => {
    if (get().status !== 'success' || api.getState().regions.propertyId !== data.id) {
      return undefined;
    }

    const requestId = ++latestRequestId;

    try {
      const { catalogCrops, filteredData } = await fetchSeasons(data, { requireComplete: true });
      if (requestId !== latestRequestId) return undefined;

      const previousSeasonId = get().selectedSeasonId;

      set((state) => {
        // Uploaded seasons come from the analytics BFF, not from the seasons API.
        const uploaded = Object.values(state.entities).filter((season): season is FieldSeasonData =>
          Boolean(season?.uploaded)
        );
        const next = seasonAdapter.upsertMany(uploaded, seasonAdapter.addAll(filteredData, state));

        return {
          ...next,
          catalogCrops,
          selectedSeasonId:
            previousSeasonId && next.entities[previousSeasonId] ? previousSeasonId : undefined,
        };
      });

      if (previousSeasonId && !get().selectedSeasonId) {
        dispatchLoadAssetsAndImages(data.id);
      }

      return Object.values(get().entities).filter((season): season is FieldSeasonData =>
        Boolean(season)
      );
    } catch (error) {
      if (requestId !== latestRequestId) return undefined;

      datadogLogs.logger.error('seasons.refresh: failed to reload the property seasons', {
        error: error instanceof Error ? error.message : String(error),
        propertyId: data.id,
      });

      return undefined;
    }
  },
  updateOptions: (options) => {
    const previousSeasonId = get().selectedSeasonId;
    set(() => ({ ...options }));

    if (!('selectedSeasonId' in options)) return;
    if (previousSeasonId === options.selectedSeasonId) return;

    const propertyId = api.getState().regions.propertyId;
    if (!propertyId) return;

    dispatchLoadAssetsAndImages(propertyId);
  },
  updateSeasons: (uploadedFieldSeasons) => {
    set((state) => ({ ...seasonAdapter.upsertMany(uploadedFieldSeasons, state) }));
  },
});
